import { tryParseDate } from '@shared/functions/try-parse-date';

export type WorkingTimeBreakRule = 'legal_30_45';
export type WorkingTimeMonthlyClosingBy = 'employee' | 'supervisor' | 'admin';

export interface BcmWorkingTimeModelDayDto {
    id?: number;
    weekday: number;
    hours: number;
}

export interface BcmWorkingTimeModelDto {
    id?: number;
    name: string;
    description?: string | null;
    is_active?: boolean;
    weekly_hours?: number;
    default_daily_hours?: number;
    holiday_federal_state?: string | null;
    break_rule?: WorkingTimeBreakRule;
    allow_multiple_breaks?: boolean | number;
    auto_check_minimum_break?: boolean;
    gps_tracking_enabled?: boolean;
    gps_record_on_clock?: boolean;
    gps_record_on_break?: boolean;
    monthly_closing_by?: WorkingTimeMonthlyClosingBy;
    days?: BcmWorkingTimeModelDayDto[];
    insertedOn?: string;
    insertedBy?: string;
    lastUpdateOn?: string | null;
    lastUpdateBy?: string | null;
}

export class BcmWorkingTimeModelDay {
    id?: number;
    weekday: number;
    hours: number;

    constructor(dto: BcmWorkingTimeModelDayDto = {} as BcmWorkingTimeModelDayDto) {
        this.id = dto.id;
        this.weekday = dto.weekday;
        this.hours = Number(dto.hours ?? 0);
    }
}

export class BcmWorkingTimeModel {
    id?: number;
    name: string;
    description: string | null;
    isActive: boolean;
    weeklyHours: number;
    defaultDailyHours: number;
    holidayFederalState: string | null;
    breakRule: WorkingTimeBreakRule;
    allowMultipleBreaks: boolean;
    autoCheckMinimumBreak: boolean;
    gpsTrackingEnabled: boolean;
    gpsRecordOnClock: boolean;
    gpsRecordOnBreak: boolean;
    monthlyClosingBy: WorkingTimeMonthlyClosingBy;
    days: BcmWorkingTimeModelDay[];
    insertedOn: Date | null;
    insertedBy: string;
    lastUpdateOn: Date | null;
    lastUpdateBy: string | null;

    constructor(dto: BcmWorkingTimeModelDto = {} as BcmWorkingTimeModelDto) {
        this.id = dto.id;
        this.name = dto.name;
        this.description = dto.description ?? null;
        this.isActive = dto.is_active ?? true;
        this.weeklyHours = Number(dto.weekly_hours ?? 0);
        this.defaultDailyHours = Number(dto.default_daily_hours ?? 0);
        this.holidayFederalState = dto.holiday_federal_state ?? null;
        this.breakRule = dto.break_rule ?? 'legal_30_45';
        // API delivers the MariaDB tinyint as 0/1; 0 must stay "off" for the break policy.
        this.allowMultipleBreaks = dto.allow_multiple_breaks == null ? true : Boolean(Number(dto.allow_multiple_breaks));
        this.autoCheckMinimumBreak = dto.auto_check_minimum_break ?? true;
        this.gpsTrackingEnabled = dto.gps_tracking_enabled ?? false;
        this.gpsRecordOnClock = dto.gps_record_on_clock ?? false;
        this.gpsRecordOnBreak = dto.gps_record_on_break ?? false;
        this.monthlyClosingBy = dto.monthly_closing_by ?? 'supervisor';
        this.days = (dto.days ?? []).map(day => new BcmWorkingTimeModelDay(day));
        this.insertedOn = tryParseDate(dto.insertedOn);
        this.insertedBy = dto.insertedBy;
        this.lastUpdateOn = tryParseDate(dto.lastUpdateOn);
        this.lastUpdateBy = dto.lastUpdateBy ?? null;
    }

    get summaryLabel(): string {
        if (!this.name) {
            return '';
        }

        return `${this.name} (${this.weeklyHours}h)`;
    }

    toPayload(): BcmWorkingTimeModelDto {
        return {
            name: this.name,
            description: this.description,
            is_active: this.isActive,
            weekly_hours: this.weeklyHours,
            default_daily_hours: this.defaultDailyHours,
            holiday_federal_state: this.holidayFederalState,
            break_rule: this.breakRule,
            allow_multiple_breaks: this.allowMultipleBreaks,
            auto_check_minimum_break: this.autoCheckMinimumBreak,
            gps_tracking_enabled: this.gpsTrackingEnabled,
            gps_record_on_clock: this.gpsRecordOnClock,
            gps_record_on_break: this.gpsRecordOnBreak,
            monthly_closing_by: this.monthlyClosingBy,
            days: this.days.map(day => ({ weekday: day.weekday, hours: day.hours })),
        };
    }

    toString(): string {
        return this.summaryLabel || this.name || '';
    }
}
